import {
  Field,
  Flex,
  Grid,
  Heading,
  Input,
  NativeSelect,
  Separator,
  Text,
} from "@chakra-ui/react";
import { useCallback, useState } from "react";

import PhoneInput from "@components/common/PhoneInput";
import { isValidPhone } from "lib/phoneUtils";
import {
  getBirthYearError,
  HEAR_OPTIONS,
  REASON_OPTIONS,
  inputProps,
  selectFieldProps,
} from "@features/services/travel/types";
import type { Step1Data } from "@features/services/travel/types";

type TouchedFields = Record<keyof Step1Data, boolean>;

const UNTOUCHED: TouchedFields = {
  name: false,
  phone: false,
  yearOfBirth: false,
  profession: false,
  reasonForTravel: false,
  howDidYouHear: false,
};

const TellUsAboutYourselfStep = ({
  data,
  onChange,
  showAllErrors,
}: {
  data: Step1Data;
  onChange: <K extends keyof Step1Data>(key: K, value: Step1Data[K]) => void;
  showAllErrors: boolean;
}) => {
  const [touched, setTouched] = useState<TouchedFields>(UNTOUCHED);

  const onBlur = useCallback((field: keyof Step1Data) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const shouldShow = useCallback(
    (field: keyof Step1Data) => touched[field] || showAllErrors,
    [touched, showAllErrors],
  );

  const nameEmpty = shouldShow("name") && data.name.trim() === "";
  const phoneEmpty = shouldShow("phone") && data.phone.trim() === "";
  const phoneInvalid =
    shouldShow("phone") && data.phone !== "" && !isValidPhone(data.phone);
  const phoneError = phoneEmpty || phoneInvalid;
  const yearEmpty = shouldShow("yearOfBirth") && data.yearOfBirth.trim() === "";
  const birthYearError = getBirthYearError(data.yearOfBirth);
  const birthYearInvalid =
    shouldShow("yearOfBirth") && (yearEmpty || birthYearError != null);
  const professionEmpty =
    shouldShow("profession") && data.profession.trim() === "";

  return (
    <Flex flexDirection="column" gap={6} mt={8}>
      <Flex flexDirection="column" gap={1}>
        <Heading size="lg" color="fg">
          Tell us about yourself
        </Heading>
        <Text color="text.secondary" fontSize="sm">
          Fill in your personal details to get started. Fields marked with * are
          required.
        </Text>
      </Flex>

      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
        <Field.Root required invalid={nameEmpty}>
          <Field.Label>
            Name <Field.RequiredIndicator />
          </Field.Label>
          <Input
            placeholder="Your full name"
            value={data.name}
            onChange={(e) => onChange("name", e.target.value)}
            onBlur={() => onBlur("name")}
            {...inputProps}
          />
          {nameEmpty && <Field.ErrorText>Name is required</Field.ErrorText>}
        </Field.Root>
        <Field.Root required invalid={phoneError}>
          <Field.Label>
            Phone number <Field.RequiredIndicator />
          </Field.Label>
          <PhoneInput
            value={data.phone}
            onChange={(val) => onChange("phone", val)}
            onBlur={() => onBlur("phone")}
            borderColor={inputProps.borderColor}
          />
          {phoneEmpty && (
            <Field.ErrorText>Phone number is required</Field.ErrorText>
          )}
          {phoneInvalid && (
            <Field.ErrorText>Enter a valid phone number</Field.ErrorText>
          )}
        </Field.Root>
        <Field.Root required invalid={birthYearInvalid}>
          <Field.Label>
            Year of birth <Field.RequiredIndicator />
          </Field.Label>
          <Input
            placeholder="e.g. 1990"
            value={data.yearOfBirth}
            onChange={(e) => onChange("yearOfBirth", e.target.value)}
            onBlur={() => onBlur("yearOfBirth")}
            {...inputProps}
          />
          {birthYearInvalid && (
            <Field.ErrorText>
              {yearEmpty ? "Year of birth is required" : birthYearError}
            </Field.ErrorText>
          )}
        </Field.Root>
        <Field.Root required invalid={professionEmpty}>
          <Field.Label>
            Profession <Field.RequiredIndicator />
          </Field.Label>
          <Input
            placeholder="Your profession"
            value={data.profession}
            onChange={(e) => onChange("profession", e.target.value)}
            onBlur={() => onBlur("profession")}
            {...inputProps}
          />
          {professionEmpty && (
            <Field.ErrorText>Profession is required</Field.ErrorText>
          )}
        </Field.Root>
      </Grid>

      <Separator borderColor="border.dark" />

      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
        <Field.Root>
          <Field.Label>Reason for travel to China</Field.Label>
          <NativeSelect.Root>
            <NativeSelect.Field
              value={data.reasonForTravel}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                onChange("reasonForTravel", e.target.value)
              }
              {...selectFieldProps}
            >
              {REASON_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Field.Root>
        <Field.Root>
          <Field.Label>How did you hear of us?</Field.Label>
          <NativeSelect.Root>
            <NativeSelect.Field
              value={data.howDidYouHear}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                onChange("howDidYouHear", e.target.value)
              }
              {...selectFieldProps}
            >
              {HEAR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Field.Root>
      </Grid>
    </Flex>
  );
};

export default TellUsAboutYourselfStep;
